import React from 'react';

import { tv } from '../../tv';
import { buttonStyles } from '../button/button';

const GRIP_PATH =
  'M3.6 2.15a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Zm0 4.8a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Zm0 4.8a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Zm4.8-9.6a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Zm0 4.8a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Zm0 4.8a.95.95 0 1 1 0-1.9.95.95 0 0 1 0 1.9Z';

const dragHandleStyles = tv({
  /* The field's height for the item's first line, and only the grip's width plus a
   * 4px inset beside it: a handle is a narrow column, not a square action. */
  base: 'ck:w-5 ck:cursor-grab ck:touch-none',
  variants: {
    isDragging: { true: 'ck:cursor-grabbing' },
  },
});

export interface DragHandleProps {
  /**
   * Names what dragging moves, read aloud and shown as the handle's name:
   * "Reorder question", "Reorder option".
   */
  label: string;
  /**
   * A sortable's attributes and listeners together, spread onto the button
   * under its own name and type. Only the keys dnd-kit sets on a handle reach it.
   */
  props?: Pick<
    React.HTMLAttributes<HTMLButtonElement>,
    | 'role'
    | 'tabIndex'
    | 'aria-disabled'
    | 'aria-pressed'
    | 'aria-roledescription'
    | 'aria-describedby'
    | 'onPointerDown'
    | 'onMouseDown'
    | 'onTouchStart'
    | 'onKeyDown'
  >;
  /**
   * Whether this item is being dragged: the cursor closes its grip, and
   * Escape cancels the drag without reaching the drawer or modal around it.
   */
  isDragging?: boolean;
}

/**
 * ComplyKit DragHandle: the grip an item in a reorderable list is dragged
 * by, or moved with the keyboard (Space, then the arrows).
 *
 * One handle everywhere: a ghost IconButton at the medium step, from
 * Button's own recipe, the height of a field so it sits on the first line of
 * the item it starts, and narrowed to the grip so it reads as the row's edge
 * rather than one more action. Its grip is always vertical. A native button
 * rather than React Aria's: dnd-kit owns its pointer and keyboard events, and
 * React Aria's press handling would contest them; outside React Aria the
 * recipe's hover and focus variants fall back to the native states. Always visible:
 * a handle that appears only on hover would leave touch with no way to
 * reorder, and a hover reveal is an enhancement, never the only signal
 * (interaction.md). The ref is the handle, for dnd-kit's
 * `setActivatorNodeRef`.
 */
/* The Escapes the handle has already handed to the drag, so it does not take them again. */
const handedToDrag = new WeakSet<Event>();

export const DragHandle = React.forwardRef<HTMLButtonElement, DragHandleProps>(function DragHandle(
  { label, props, isDragging = false },
  ref,
) {
  React.useEffect(() => {
    if (!isDragging) return;
    /* A host's Escape handler runs before dnd-kit's document listener and would close the host
     * mid-drag: the key is taken first, in capture, and handed to the document alone. */
    const takeEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || handedToDrag.has(event)) return;
      event.stopImmediatePropagation();
      event.preventDefault();
      const handed = new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape' });
      handedToDrag.add(handed);
      document.dispatchEvent(handed);
    };
    window.addEventListener('keydown', takeEscape, true);
    return () => window.removeEventListener('keydown', takeEscape, true);
  }, [isDragging]);

  const { root } = buttonStyles({
    variant: 'ghost',
    tone: 'neutral',
    size: 'md',
    shape: 'square',
    pending: false,
  });

  return (
    <button
      {...props}
      ref={ref}
      type="button"
      aria-label={label}
      className={root({ className: dragHandleStyles({ isDragging }) })}
      data-component="drag-handle"
      data-tone="neutral"
    >
      <svg
        viewBox="0 0 12 12"
        width="12"
        height="12"
        fill="currentColor"
        aria-hidden="true"
        focusable="false"
      >
        <path d={GRIP_PATH} />
      </svg>
    </button>
  );
});
